/**
 * useFilters.js
 *
 * Faceted product-filter hook. AND between facets, OR within a facet.
 * Computes dynamic per-facet counts that respect every OTHER facet's
 * current selection (so users see "12" next to a value only when 12 items
 * would survive if they ticked it).
 *
 * Ported from TH ToolFinder v4 (see
 * `Top Hammer Toolfinder/tophammer-toolfinder-v4/src/hooks/useFilters.js`,
 * 183 lines). Behavioural ports:
 *   - String-equality comparison (TF v4 wraps both sides in `String(...)`).
 *   - Counts ignore null / '' / '-' values.
 *   - Sort facet values by descending count.
 *   - `clearAllFilters`, `setFilter`, `setFilters`, `toggleFilterValue`,
 *     `activeFilterCount` API surface preserved 1:1.
 *
 * Adaptation: TF v4's source carries scattered `console.log` debug calls
 * left over from F-006 troubleshooting. The brief (`STATUS-2026-05-17-EOD.md`,
 * §"strip those — production noise") instructs the port to drop them.
 * No behaviour change.
 *
 * @module hooks/useFilters
 * @since 2026-05-17 (Group D Sub-Plan 3, Task SP3.A.1)
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * @param {Array} products - Array of products to filter.
 * @param {Array} facetConfig - Facet configuration array (id, label, field).
 * @returns {Object} Filter state and methods.
 */
export function useFilters(products = [], facetConfig = []) {
  const [activeFilters, setActiveFilters] = useState({});

  // Defensive: callers may pass undefined during initial render.
  const safeProducts = Array.isArray(products) ? products : [];
  const facets = Array.isArray(facetConfig) ? facetConfig : [];

  // Filter products: AND between facets, OR within facet.
  const filteredProducts = useMemo(() => {
    if (safeProducts.length === 0) return [];

    const activeFilterIds = Object.keys(activeFilters)
      .filter(id => activeFilters[id]?.length > 0);

    if (activeFilterIds.length === 0) return safeProducts;

    return safeProducts.filter(product => {
      for (const facetId of activeFilterIds) {
        const facet = facets.find(f => f.id === facetId);
        if (!facet) continue;

        const selectedValues = activeFilters[facetId];
        const productValue = product[facet.field];

        let matchesAny = false;
        for (const value of selectedValues) {
          if (String(productValue) === String(value)) {
            matchesAny = true;
            break;
          }
        }

        if (!matchesAny) return false;
      }
      return true;
    });
  }, [safeProducts, activeFilters, facets]);

  // Per-facet counts: each facet's counts are computed as if ONLY the OTHER
  // facets' selections were applied. This is the "dynamic count respects
  // other facets" behaviour from TF v4 and the original Interactive Catalog.
  const facetsWithCounts = useMemo(() => {
    if (facets.length === 0) return [];

    if (safeProducts.length === 0) {
      return facets.map(facet => ({
        ...facet,
        values: [],
        selectedValues: activeFilters[facet.id] || [],
      }));
    }

    return facets.map(facet => {
      const counts = {};

      // Apply every filter EXCEPT this facet's own.
      const otherFilters = { ...activeFilters };
      delete otherFilters[facet.id];
      const otherFilterIds = Object.keys(otherFilters)
        .filter(id => otherFilters[id]?.length > 0);

      let baseProducts = safeProducts;
      if (otherFilterIds.length > 0) {
        baseProducts = safeProducts.filter(product => {
          if (!product) return false;
          for (const fId of otherFilterIds) {
            const f = facets.find(x => x.id === fId);
            if (!f) continue;
            const selectedValues = otherFilters[fId];
            const productValue = product[f.field];
            const matchesAny = selectedValues.some(
              v => String(productValue) === String(v),
            );
            if (!matchesAny) return false;
          }
          return true;
        });
      }

      // Tally occurrences of the facet's own field.
      baseProducts.forEach(product => {
        if (!product) return;
        const value = product[facet.field];
        if (value != null && value !== '' && value !== '-') {
          counts[value] = (counts[value] || 0) + 1;
        }
      });

      // Sort by descending count for stable, scannable rendering.
      const values = Object.entries(counts)
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count);

      return { ...facet, values, selectedValues: activeFilters[facet.id] || [] };
    });
  }, [safeProducts, facets, activeFilters]);

  /**
   * Toggle a single value on/off inside a facet.
   */
  const toggleFilterValue = useCallback((facetId, value) => {
    setActiveFilters(prev => {
      const current = prev[facetId] || [];
      const index = current.indexOf(value);
      if (index >= 0) {
        return { ...prev, [facetId]: current.filter(v => v !== value) };
      }
      return { ...prev, [facetId]: [...current, value] };
    });
  }, []);

  /**
   * Replace the value-array for one facet.
   */
  const setFilter = useCallback((facetId, values) => {
    setActiveFilters(prev => ({ ...prev, [facetId]: values }));
  }, []);

  /**
   * Replace the entire filter map.
   */
  const setFilters = useCallback((newFilters) => {
    setActiveFilters(newFilters);
  }, []);

  /**
   * Drop every selection.
   */
  const clearAllFilters = useCallback(() => {
    setActiveFilters({});
  }, []);

  /**
   * Total count of selected values across all facets.
   */
  const activeFilterCount = useMemo(() => {
    return Object.values(activeFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0,
    );
  }, [activeFilters]);

  return {
    filteredProducts,
    facetsWithCounts,
    activeFilters,
    toggleFilterValue,
    setFilter,
    setFilters,
    clearAllFilters,
    activeFilterCount,
  };
}

export default useFilters;
